import { TypedDocumentNode, gql } from '@apollo/client';
import { useState } from 'react';

import { isType } from '@sorare/core/src/lib/gql';

import { RewardsFlow } from './RewardsFlow';
import type { LastFixtureStory_cardXpGradeThreshold } from './__generated__/index.graphql';
import { LastFixtureStory_story } from './__generated__/index.graphql';
import { useDisplayFixtureStory } from './useDisplayFixtureStory';

type LastFixtureStoryProps = {
  story: Nullable<LastFixtureStory_story>;
  gradeThresholds?: LastFixtureStory_cardXpGradeThreshold[];
};
export const LastFixtureStory = ({
  story,
  gradeThresholds,
}: LastFixtureStoryProps) => {
  const [storyOpened, setStoryOpened] = useState(true);
  const displayFixtureStory = useDisplayFixtureStory(story);

  const {
    id,
    readable: so5Fixture,
    leveledUpCards,
    completedThresholdsStreakTasks,
    completedFtueOnboardingTasks,
  } = story || {};

  if (
    !displayFixtureStory ||
    !id ||
    !so5Fixture ||
    !isType(so5Fixture, 'So5Fixture') ||
    !storyOpened
  ) {
    return null;
  }

  return (
    <RewardsFlow
      storyId={id}
      so5Fixture={so5Fixture}
      completedThresholdsStreakTasks={completedThresholdsStreakTasks}
      completedOnboardingTasks={completedFtueOnboardingTasks}
      leveledUpCards={leveledUpCards}
      gradeThresholds={gradeThresholds}
      so5Rewards={so5Fixture.mySo5Rewards}
      onClose={() => setStoryOpened(false)}
    />
  );
};

LastFixtureStory.fragments = {
  cardXpGradeThreshold: gql`
    fragment LastFixtureStory_cardXpGradeThreshold on CardXPGradeThreshold {
      ...RewardsFlow_cardXpGradeThreshold
    }
    ${RewardsFlow.fragments.cardXpGradeThreshold}
  ` as TypedDocumentNode<LastFixtureStory_cardXpGradeThreshold>,
  story: gql`
    fragment LastFixtureStory_story on Story {
      id
      aasmState
      completedThresholdsStreakTasks {
        id
        ...RewardsFlow_thresholdsStreakTask
      }
      completedFtueOnboardingTasks {
        id
        ...RewardsFlow_taskInterface
      }
      leveledUpCards {
        id
        ...RewardsFlow_leveledUpCard
      }
      readable {
        ... on FootballRivalsChallenge {
          id
        }
        ... on So5Fixture {
          slug
          mySo5Rewards {
            slug
            ...RewardsFlow_so5Reward
          }
        }
        ...RewardsFlow_so5Fixture
      }
      ...useDisplayFixtureStory_story
    }
    ${RewardsFlow.fragments.so5Reward}
    ${RewardsFlow.fragments.so5Fixture}
    ${RewardsFlow.fragments.leveledUpCard}
    ${RewardsFlow.fragments.thresholdsStreakTask}
    ${RewardsFlow.fragments.taskInterface}
    ${useDisplayFixtureStory.fragments.story}
  ` as TypedDocumentNode<LastFixtureStory_story>,
};
